import { useCallback, useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";

import { exportInventoryCSV, exportInventoryXLSX, getSparepartList } from "api/inventorySpareparts";
import BaseTable from "components/DataTable/BaseTable";
import ExportButton from "components/ExportButton";
import InventoryKPICards from "components/InventoryKPICards";
import ListingImageCol from "components/ListingImageCol";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import SparePartTagSelector from "components/SparePartTagSelector";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import useFilterStore from "store/useFilterStore";
import { formatDateTime, STATUS_ACTIVE } from "utils/helper";

const TABLE_COLUMNS = [
    { id: "name", Header: "Name", accessor: "name", align: "left", sortable: true },
    { id: "stock", Header: "Stock", accessor: "stock", align: "left", sortable: true },
    { id: "minimum", Header: "Minimum", accessor: "minimum", align: "left", sortable: true },
    { id: "desired", Header: "Desired", accessor: "desired", align: "left", sortable: true },
    { id: "tag", Header: "Tag", accessor: "tag", align: "left", sortable: true },
    { id: "ride", Header: "Ride", accessor: "ride", align: "left", sortable: true },
    // { id: "unit_cost", Header: "Unit Cost(RM)", accessor: "unit_cost", align: "left", sortable: true },
    // { id: "unit_of_measure", Header: "Unit Measures", accessor: "unit_of_measure", align: "left" },
    { id: "part_number", Header: "Part Number", accessor: "part_number", align: "left" },
    { id: "type", Header: "Type", accessor: "type", align: "left" },
    { id: "storeroom", Header: "Storeroom", accessor: "storeroom", align: "left" },
    { id: "qr_code", Header: "QR Code", accessor: "qr_code", align: "left" },
    { id: "created_at", Header: "Created At", accessor: "created_at", align: "left", sortable: true },
    { id: "status", Header: "Status", accessor: "status", align: "left" },
];

export const parentRoute = '/spare-parts';

function SparePartsTable() {
    const navigate = useNavigate();
    const location = useLocation();
    const { getFilters, setFilters } = useFilterStore();
    const [selectedTag, setSelectedTag] = useState("");
    const [loading, setLoading] = useState(false);
    const currentPath = window.location.pathname;
    const savedState = location.state || { sort: "created_at", dir: "desc" };

    useEffect(() => {
        const record = getFilters(currentPath);
        if (record?.customParams) {
            if (record.customParams.selectedTag !== undefined) setSelectedTag(record.customParams.selectedTag);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Persist custom filters whenever they change (merge with existing baseTable state)
    useEffect(() => {
        const record = getFilters(currentPath);
        setFilters(currentPath, {
            baseTable: record.baseTable, // preserve the table paging/sort managed by BaseTable
            customParams: { selectedTag }
        });
    }, [selectedTag, currentPath]);

    const createTableRow = useCallback((item) => ({
        name: <ListingImageCol imgUrl={item.img_filename ?? item.images?.[0]?.url} name={item.name} />,
        stock: <MDTypography component="div" variant="standard" color="text">{item.stock ?? 0}</MDTypography>,
        minimum: <MDTypography component="div" variant="standard" color="text">{item.minimum_quantity ?? '-'}</MDTypography>,
        desired: <MDTypography component="div" variant="standard" color="text">{item.desired_quantity ?? '-'}</MDTypography>,
        tag: <MDTypography component="div" variant="standard" color="text">{item.tag ?? "-"}</MDTypography>,
        ride: <MDTypography component="div" variant="standard" color="text">{item.ride_id?.name ?? '-'}</MDTypography>,
        // unit_cost: <MDTypography component="div" variant="standard" color="text">{item.unit_cost ?? '-'}</MDTypography>,
        // unit_of_measure: <MDTypography component="div" variant="standard" color="text">{item.unit_of_measure ?? '-'}</MDTypography>,
        part_number: <MDTypography component="div" variant="standard" color="text">{item.part_number ?? '-'}</MDTypography>,
        type: <MDTypography component="div" variant="standard" color="text">{item.type?.name ?? '-'}</MDTypography>,
        storeroom: <MDTypography component="div" variant="standard" color="text">{item.storeroom ?? '-'}</MDTypography>,
        created_at: <MDTypography component="div" variant="standard" color="text">
            {formatDateTime(item.created_at)}
        </MDTypography>,
        qr_code: <MDTypography component="div" variant="standard" color="text">{item.qr_code ?? '-'}</MDTypography>,
        status: <MDTypography component="div" variant="standard" color={item.status?.id === STATUS_ACTIVE ? "success" : "error"}>
            {item.status?.name}
        </MDTypography>
    }), []);

    const fetchWithFilters = useCallback(async (params = {}) => {
        const filterParams = {
            ...params,
            ...(selectedTag ? { tag: selectedTag, page: 1 } : {}),
        };
        return getSparepartList(filterParams);
    }, [selectedTag]);

    const filters = (
        <>
            <MDBox sx={{ flex: 1, minWidth: 150 }}>
                <SparePartTagSelector
                    label="Tag"
                    value={selectedTag}
                    onChange={setSelectedTag}
                    isFilter
                />
            </MDBox>
        </>
    );

    const handleExport = useCallback((format) => {
        setLoading(true);
        const filteredParams = getFilters(currentPath);
        const exportFn = format === 'csv' ? exportInventoryCSV : exportInventoryXLSX;
        exportFn({ ...filteredParams.baseTable, ...filteredParams.customParams }).finally(() => setLoading(false));
    }, [getFilters(currentPath)]);


    return (
        <DashboardLayout>
            <DashboardNavbar hasKPI />
            {/* KPI Statistics Cards */}
            <InventoryKPICards />

            <BaseTable
                title="Spare Parts Management"
                columns={TABLE_COLUMNS}
                fetchData={fetchWithFilters}
                defaultState={savedState}
                createTableRow={createTableRow}
                showAddButton
                addButtonText="Add Spare Part"
                onAddClick={() => navigate(`${parentRoute}/add-spare-part`, { state: savedState })}
                showSearch
                showStatusFilter
                showRideFilter
                showFrequencyTypeFilter
                searchPlaceholder="Search spare part name..."
                additionalFilters={filters}
                onClickRow={(row, key) => { navigate(`${parentRoute}/${row.id}`, { state: { ...savedState, item: row?.oriData } }) }}
                customButton={<ExportButton handleExport={handleExport} loading={loading} />}
            />
        </DashboardLayout>
    );
}

export default SparePartsTable;